export interface TokenDeltaEntry {
  costDeltaUsd: number;
  timestampMs: number;
}

export const WINDOW_MS = 300_000;
export const MS_PER_HOUR = 3_600_000;
export const RECALCULATE_INTERVAL_MS = 1000;

interface RealtimeLikeMessage {
  payload?: unknown;
  timestamp?: unknown;
  type?: unknown;
}

export function shouldTrackBurnRate(instanceStatus?: string): boolean {
  return instanceStatus !== "waiting" && instanceStatus !== "killed";
}

export function calculateBurnRateUsdPerHr(
  entries: TokenDeltaEntry[],
  nowMs: number,
): { entries: TokenDeltaEntry[]; burnRateUsdPerHr: number | null } {
  const cutoff = nowMs - WINDOW_MS;
  const activeEntries = entries.filter((entry) => entry.timestampMs >= cutoff);

  if (activeEntries.length === 0) {
    return { burnRateUsdPerHr: null, entries: activeEntries };
  }

  const windowCost = activeEntries.reduce(
    (sum, entry) => sum + entry.costDeltaUsd,
    0,
  );
  return {
    burnRateUsdPerHr: (windowCost / WINDOW_MS) * MS_PER_HOUR,
    entries: activeEntries,
  };
}

export function getTokenDeltaEntryForInstance(
  message: RealtimeLikeMessage,
  instanceId: string | null,
): TokenDeltaEntry | null {
  if (!instanceId || message.type !== "instance_activity") {
    return null;
  }

  if (typeof message.timestamp !== "number") {
    return null;
  }

  const payload =
    typeof message.payload === "object" && message.payload !== null
      ? message.payload
      : null;
  if (!payload) {
    return null;
  }

  const instanceIdValue = "instance_id" in payload ? payload.instance_id : null;
  if (typeof instanceIdValue !== "string" || instanceIdValue !== instanceId) {
    return null;
  }

  const activityType =
    "activity_type" in payload ? payload.activity_type : undefined;
  if (activityType !== "token_delta") {
    return null;
  }

  const detail =
    "detail" in payload &&
    typeof payload.detail === "object" &&
    payload.detail !== null
      ? payload.detail
      : null;
  if (!detail) {
    return null;
  }

  const costDeltaUsd =
    "cost_delta_usd" in detail && typeof detail.cost_delta_usd === "number"
      ? detail.cost_delta_usd
      : 0;

  return { costDeltaUsd, timestampMs: message.timestamp };
}
